import { MapStore } from "@infrastructure/stores/map.store";
import { YMap } from "@yandex/ymaps3-types";

class MapServiceClass {
  getStore() {
    return MapStore;
  }

  /**
   * Устанавливает карту
   */
  setMap(map: YMap | null) {
    MapStore.setMap(map);
  }

  /**
   * Устанавливает локацию карты
   */
  setLocation(location: { center: [number, number]; zoom: number }) {
    MapStore.setLocation(location);
  }

  /**
   * Устанавливает локацию пользователя
   */
  setUserLocation(location: { latitude: number; longitude: number } | null) {
    MapStore.setUserLocation(location);
  }

  /**
   * Устанавливает видимость карты
   */
  setVisibility(isVisible: boolean) {
    MapStore.setVisibility(isVisible);
  }

  /**
   * Устанавливает флаг инициализации
   */
  setInitialized(initialized: boolean) {
    MapStore.setInitialized(initialized);
  }

  /**
   * Получает текущую геолокацию
   */
  async getCurrentLocation() {
    return MapStore.getCurrentLocation();
  }

  /**
   * Инициализирует локацию карты (только один раз)
   * Если аппарат 1 - центрируем на нем с зумом 15
   * Если аппаратов несколько - центрируем между ними и подбираем зум
   */
  async initializeLocation() {
    const state = MapStore.getState();
    if (state.hasInitialized) {
      console.log('[MapService] Location already initialized, skipping');
      return;
    }
    
    try {
      // Получаем список аппаратов
      const { MachineService } = await import("@domain/services/machine.service");
      const machines = MachineService.getStore().getState().items;
      console.log('[MapService] Initializing location for', machines.length, 'machines');
      
      // Фильтруем аппараты с координатами
      const machinesWithCoords = machines.filter(m => m.latitude !== null && m.longitude !== null);
      console.log('[MapService] Machines with coords:', machinesWithCoords.length);
      
      if (machinesWithCoords.length === 0) {
        // Нет аппаратов - используем геолокацию пользователя
        console.log('[MapService] No machines, using user location');
        await this.getCurrentLocation();
      } else if (machinesWithCoords.length === 1) {
        // Один аппарат - центрируем на нем с зумом 15
        const machine = machinesWithCoords[0];
        console.log('[MapService] Single machine, centering at:', [machine.longitude, machine.latitude], 'zoom: 15');
        this.setLocation({
          center: [machine.longitude!, machine.latitude!],
          zoom: 15
        });
      } else {
        // Несколько аппаратов - центрируем между ними и подбираем зум
        const lats = machinesWithCoords.map(m => m.latitude!);
        const lngs = machinesWithCoords.map(m => m.longitude!);
        
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        // Центр между аппаратами
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        
        // Подбираем зум так, чтобы все аппараты были видны
        const latDelta = maxLat - minLat;
        const lngDelta = maxLng - minLng;
        const maxDelta = Math.max(latDelta, lngDelta);
        
        // Грубая оценка зума (чем больше расстояние, тем меньше зум)
        let zoom = 15;
        if (maxDelta > 0.1) zoom = 11;       // > 11 км
        else if (maxDelta > 0.05) zoom = 12; // > 5.5 км
        else if (maxDelta > 0.02) zoom = 13; // > 2.2 км
        else if (maxDelta > 0.01) zoom = 14; // > 1.1 км
        
        console.log('[MapService] Multiple machines, centering at:', [centerLng, centerLat], 'zoom:', zoom);
        this.setLocation({
          center: [centerLng, centerLat],
          zoom
        });
      }
      
      this.setInitialized(true);
      console.log('[MapService] Location initialized successfully');
    } catch (error) {
      console.error('[MapService] Error initializing location:', error);
      // Fallback - используем геолокацию
      await this.getCurrentLocation();
      this.setInitialized(true);
    }
  }
}

export const MapService = new MapServiceClass();

